import { useQuery } from 'data-fetching-client';
import { GET_IN_APP_CURRENCIES } from 'pricing-pages-api/currency/queries';
import { useCallback } from 'react';
import { useGetCurrencyStore, useSetCurrencyStore } from '../../../context/CurrencyContext';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { isCurrencyCodeString } from 'pricing-pages-api/currency/types';
const UseSetLoadingCurrencyComplete = () => {
  const setStore = useSetCurrencyStore();
  const handleCurrencyFetchCompleted = ({
    currencies
  }) => {
    setStore(currencies);
  };
  return handleCurrencyFetchCompleted;
};
export const useCurrencies = () => {
  const handleCurrencyFetchCompleted = UseSetLoadingCurrencyComplete();
  const setStore = useSetCurrencyStore();
  const version = 'v2';
  const handleCompleted = useCallback(data => {
    logApiSuccess({
      queryName: 'GET_IN_APP_CURRENCIES',
      operation: 'getInAppCurrencies',
      version
    });
    handleCurrencyFetchCompleted(data);
  }, [handleCurrencyFetchCompleted, version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName: 'GET_IN_APP_CURRENCIES',
      operation: 'getInAppCurrencies',
      version
    });
    // Populate the store with a USD fallback so downstream queries gated on
    // useIsDisplayedCurrencyCodeReady don't hang forever when the currency
    // RPC fails.
    setStore({
      displayedCurrencyCode: 'USD'
    });
  }, [version, setStore]);
  const {
    data,
    error,
    loading
  } = useQuery(GET_IN_APP_CURRENCIES, {
    variables: {},
    onCompleted: handleCompleted,
    onError: handleError
  });
  const currencyData = data && data.currencies;
  return {
    currencyData,
    error,
    loading
  };
};
export const useDisplayedCurrencyCode = () => {
  const currencyCode = useGetCurrencyStore(store => store.displayedCurrencyCode);
  if (isCurrencyCodeString(currencyCode)) {
    return currencyCode;
  }
  return 'USD';
};

// True once the currency RPC has populated the store. Before then,
// useDisplayedCurrencyCode falls back to 'USD' — callers that fire network
// requests keyed on currency should wait for this to avoid a USD→real-currency
// refetch.
export const useIsDisplayedCurrencyCodeReady = () => {
  const currencyCode = useGetCurrencyStore(store => store.displayedCurrencyCode);
  return isCurrencyCodeString(currencyCode);
};
export const useLockedCurrencyCode = () => {
  const currencyCode = useGetCurrencyStore(store => store.lockedCurrencyCode);
  return currencyCode;
};
export const useCurrencyCodeUnitToDisplay = () => {
  const currencyCodeUnit = useGetCurrencyStore(store => store.currencyCodeUnit);
  const showCurrencyCodeUnit = useGetCurrencyStore(store => store.showCurrencyCodeUnit);
  return showCurrencyCodeUnit ? currencyCodeUnit : undefined;
};
export const useAvailableCurrencyCodes = () => {
  const availableCurrencyCodes = useGetCurrencyStore(store => store.availableCurrencyCodes);
  return availableCurrencyCodes.filter(isCurrencyCodeString);
};
export const useCountryCode = () => {
  const countryCode = useGetCurrencyStore(store => store.countryCode);
  return countryCode || undefined;
};